/**
 * useFieldTerrain — TanStack Query hook для GET /api/v1/fields/{id}/terrain.
 *
 * Endpoint (backend/src/ikolhoz/api/v1/terrain.py, AK-205) возвращает ТРИ
 * разных исхода, которые UI должен различать:
 *   - 200 + TerrainResponse            — рельеф посчитан (terrain_status=computed)
 *   - 409 + { status, message }        — pending / failed / no_reference_contour
 *   - 404                              — поля нет в тенанте
 *
 * ⚠️ Почему НЕ через `api.get`: тело 409 у этого эндпоинта — top-level
 * `{ status, message }` (НЕ обёрнуто в `detail`). Общий api-клиент
 * (`coerceDetail(data?.detail)`) теряет структуру, и мы не смогли бы отличить
 * pending от failed. Поэтому здесь прямой fetch с чтением тела. credentials
 * include — cookie-сессия как везде.
 *
 * Cache: per field_id. staleTime короткий — pending → computed прилетает,
 * когда воркер досчитает; refetch по возврату на таб освежает статус.
 *
 * Spec: AK-207 (таб «Рельеф» в карточке поля), эпик AK-198.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { API_URL } from '@/lib/api';

export type CardinalDir = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

/** 200-тело: метрики рельефа. Зеркалит TerrainResponse (schemas/terrain.py). */
export interface TerrainMetrics {
  slope_mean: number;
  slope_min: number;
  slope_max: number;
  slope_p90: number;
  aspect_cardinal: CardinalDir;
  aspect_degrees: number;
  elevation_mean: number;
  elevation_min: number;
  elevation_max: number;
  curvature_mean: number;
  twi_mean: number;
  slope_class_0_2_pct: number;
  slope_class_2_5_pct: number;
  slope_class_5_10_pct: number;
  slope_class_10_plus_pct: number;
  computed_at: string;
  dem_source: string;
}

export type TerrainNotReadyStatus =
  | 'pending'
  | 'failed'
  | 'no_reference_contour';

/**
 * Нормализованный результат: одна из трёх ветвей. `kind` — дискриминатор,
 * UI матчит по нему без гадания по HTTP-коду.
 */
export type TerrainResult =
  | { kind: 'computed'; metrics: TerrainMetrics }
  | { kind: 'not_ready'; status: TerrainNotReadyStatus; message: string }
  | { kind: 'not_found' };

async function fetchTerrain(fieldId: string): Promise<TerrainResult> {
  const resp = await fetch(
    `${API_URL}/api/v1/fields/${fieldId}/terrain`,
    { credentials: 'include' },
  );

  if (resp.status === 404) {
    return { kind: 'not_found' };
  }

  const body = await resp.json().catch(() => ({}));

  if (resp.status === 409) {
    // { status, message } на верхнем уровне тела.
    const status = (body?.status ?? 'failed') as TerrainNotReadyStatus;
    const message =
      typeof body?.message === 'string'
        ? body.message
        : 'Метрики рельефа недоступны.';
    return { kind: 'not_ready', status, message };
  }

  if (!resp.ok) {
    // 401/403/5xx — пусть react-query покажет ошибку и ретрайнет.
    throw new Error(`terrain fetch failed: ${resp.status}`);
  }

  return { kind: 'computed', metrics: body as TerrainMetrics };
}

export function useFieldTerrain(
  fieldId: string | null | undefined,
): UseQueryResult<TerrainResult, Error> {
  return useQuery({
    queryKey: ['field-terrain', fieldId],
    queryFn: () => fetchTerrain(fieldId as string),
    enabled: !!fieldId,
    staleTime: 60_000,
    retry: 1,
  });
}
